/**
 * A conversa de um caso na tela (E149.F2 — GUS-149): lê, envia e relê. O servidor deriva a
 * situação de cada item das respostas, então depois de enviar a tela relê em vez de calcular.
 * A recusa volta como o código do servidor, para quem enviou dizer o que corrigir.
 *
 * O que é do caso zera na troca de caso: a página fica montada quando só o `:id` muda. `onEnviada`
 * avisa a página que o caso mudou (o pedido respondido tira o caso de "aguardando o cliente").
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiClient } from '@/lib/api';
import {
  ConversaError,
  enviarMensagem,
  lerConversa,
  type ConversaDoCasoData,
  type MensagemEntradaData,
  type RecusaDaConversa,
} from '@/lib/conversa_do_caso';

export interface ConversaNaTela {
  conversa: ConversaDoCasoData | null;
  /** A leitura falhou; `recusado` quando o servidor não entrega o caso à sessão. */
  erro: 'recusado' | 'falha' | null;
  recarregar: () => Promise<void>;
  /** Envia e relê; devolve `null` no sucesso ou o código da recusa. */
  enviar: (entrada: MensagemEntradaData) => Promise<RecusaDaConversa | null>;
}

function recusaDe(err: unknown): RecusaDaConversa {
  return err instanceof ConversaError ? err.recusa : 'indisponivel';
}

export function useConversaDoCaso(caseId: string, onEnviada?: () => void): ConversaNaTela {
  const client = useApiClient();
  const aoEnviar = useRef(onEnviada);
  aoEnviar.current = onEnviada;
  const [conversa, setConversa] = useState<ConversaDoCasoData | null>(null);
  const [erro, setErro] = useState<'recusado' | 'falha' | null>(null);
  const vigente = useRef(caseId);

  const recarregar = useCallback(async (): Promise<void> => {
    const doCaso = caseId;
    try {
      const lida = await lerConversa(client, doCaso);
      if (vigente.current !== doCaso) return;
      setConversa(lida);
      setErro(null);
    } catch (err) {
      if (vigente.current !== doCaso) return;
      setErro(recusaDe(err) === 'CASE_NOT_FOUND' ? 'recusado' : 'falha');
    }
  }, [client, caseId]);

  useEffect(() => {
    vigente.current = caseId;
    setConversa(null);
    setErro(null);
    void recarregar();
  }, [caseId, recarregar]);

  const enviar = useCallback(
    async (entrada: MensagemEntradaData): Promise<RecusaDaConversa | null> => {
      let recusa: RecusaDaConversa | null = null;
      try {
        await enviarMensagem(client, caseId, entrada);
      } catch (err) {
        recusa = recusaDe(err);
      }
      // A resposta muda a situação do item, e a recusa por item já respondido pede a leitura nova.
      if (recusa === null || recusa.startsWith('ITEM_')) await recarregar();
      if (recusa === null) aoEnviar.current?.();
      return recusa;
    },
    [client, caseId, recarregar],
  );

  return { conversa, erro, recarregar, enviar };
}
